/**
 * TASK-APS-004: Fog Particle Update Logic
 *
 * Updates fog particles with movement and edge wrapping.
 * Fog particles drift slowly and wrap seamlessly at the edges.
 */

import type { Particle } from './particle-pool';
import type { FogConfig } from '../../types/terrain';

/**
 * Updates a fog particle's position with edge wrapping
 *
 * Fog particles:
 * - Move by velocity * deltaTime
 * - Wrap around at edges (seamless looping)
 * - Do not fade or respawn
 *
 * @param particle - Particle to update (mutated in place)
 * @param config - Fog configuration
 * @param deltaTime - Time elapsed since last update (seconds)
 */
export function updateFogParticle(
  particle: Particle,
  config: FogConfig,
  deltaTime: number
): void {
  // Move particle by velocity
  particle.x += particle.vx * deltaTime;
  particle.y += particle.vy * deltaTime;

  // Wrap at horizontal edges
  const boundsRight = config.bounds.x + config.bounds.width;
  const boundsLeft = config.bounds.x;

  if (particle.x > boundsRight) {
    // Wrap from right to left, preserving the overflow distance
    particle.x = boundsLeft + (particle.x - boundsRight);
  } else if (particle.x < boundsLeft) {
    // Wrap from left to right, preserving the overflow distance
    particle.x = boundsRight + (particle.x - boundsLeft);
  }

  // Wrap at vertical edges
  const boundsBottom = config.bounds.y + config.bounds.height;
  const boundsTop = config.bounds.y;

  if (particle.y > boundsBottom) {
    // Wrap from bottom to top, preserving the overflow distance
    particle.y = boundsTop + (particle.y - boundsBottom);
  } else if (particle.y < boundsTop) {
    // Wrap from top to bottom, preserving the overflow distance
    particle.y = boundsBottom + (particle.y - boundsTop);
  }

  // Fog particles do not change alpha or scale during updates
}
